/**
 * Fabrique des blocs CMS avancé de la categorie "layout" (parite de rendu avec le
 * site public). Corps deplaces VERBATIM depuis config.tsx.
 *
 * i18n : AUCUN appel cmsNew(...) en portee module. Tout reste dans le corps
 * de la fabrique, re-evalue a chaque appel de createPuckConfig()
 * (useMemo([translationRevision])) pour reconstruire les libelles au
 * chargement des traductions i18next.
 */

import { MAX_CONTENT_COLUMNS } from "@/lib/cms-puck/blocks/column-count";
import { ColumnsGrid } from "@/components/cms-puck/layout/ColumnsGrid";
import { GridColumnWidthsField } from "@/components/cms-puck/fields/GridColumnWidthsField";
import { FlexAlignField, FlexJustifyField, GapIconField } from "@/components/cms-puck/fields/LayoutIconFields";
import { applyGridPreset, getGridPresetOptions, normalizeGridPreset } from "@/lib/cms-puck/blocks/grid-presets";
import { GridContainer } from "@/components/cms-puck/layout/GridContainer";
import { getColumnTrackSizeOptions, columnCellClassName, columnSlotKeys, containerClass, containerGapStyle, getLayoutGapOptions, getLayoutMaxWidthOptions, normalizeColumnsCount, resolveColumnTrackSizes, resolveContainerHeightStyle, type ColumnWidthWeight, type LayoutAlign, type LayoutGap, type LayoutJustify } from "@/lib/cms-puck/layout";
import { LayoutSlot } from "@/components/cms-puck/layout/LayoutSlot";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { type CustomField } from "@measured/puck";
import { type RawComponentsConfig } from "../types";
import { NESTABLE_SLOT_ALLOW } from "../assembly";

/**
 * « Espacement (uniforme) » doit piloter les deux axes : les réglages
 * horizontal/vertical, s'ils existent, le masqueraient (`gapRow ?? gap`). On les
 * efface donc dès que l'uniforme (preset ou px) vient d'être modifié ; ils
 * retombent sur `gap` jusqu'à ce qu'on les redéfinisse explicitement.
 */
function uniformGapResetPatch(
  changed?: { gap?: boolean; gapCustomPx?: boolean },
): Record<string, unknown> {
  if (!changed) return {};
  const patch: Record<string, unknown> = {};
  if (changed.gap || changed.gapCustomPx) {
    patch.gapRow = undefined;
    patch.gapCol = undefined;
    patch.gapRowCustomPx = null;
    patch.gapColCustomPx = null;
  }
  return patch;
}

export function buildLayoutBlocks({
  columnsLayoutCountField,
  gridColumnCountField,
}: {
  columnsLayoutCountField: CustomField<2 | 3 | 4>;
  gridColumnCountField: CustomField<number>;
}) {
  return {
    /* ------------------------------------------------------------------ */
    /* Blocs de mise en page (conteneurs à slots imbriqués)               */
    /* Toutes les props sont STRUCTURELLES (non traduisibles). Le slot     */
    /* `content` est un champ Puck natif `type: "slot"` : Puck stocke les   */
    /* blocs enfants INLINE dans `props.content` et fournit au `render` un  */
    /* composant à invoquer (`<Content />`) qui rend les enfants (éditeur   */
    /* ET public). Aperçu et rendu public visuellement cohérents (mêmes     */
    /* classes Tailwind via le helper `@/lib/cms-puck/layout`).             */
    /* ------------------------------------------------------------------ */

    Container: {
      label: cmsNew("cms.new.blocks.container.label", "Conteneur"),
      fields: {
        direction: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.direction", "Direction"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.ligne", "Ligne"),
              value: "row",
            },
            {
              label: cmsNew("cms.new.auto.label.colonne", "Colonne"),
              value: "column",
            },
          ],
        },
        wrap: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.retour_ligne", "Retour à la ligne (mode Ligne)"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.autoriser", "Autoriser"),
              value: "wrap",
            },
            {
              label: cmsNew("cms.new.auto.label.une_seule_ligne", "Une seule ligne"),
              value: "nowrap",
            },
          ],
        },
        align: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.alignement_transversal",
            "Alignement (transversal)",
          ),
          render: ({ value, onChange }) => (
            <FlexAlignField
              hideLabel
              value={(value as LayoutAlign) ?? "stretch"}
              onChange={onChange}
            />
          ),
        },
        justify: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.justification_principal",
            "Justification (principal)",
          ),
          render: ({ value, onChange }) => (
            <FlexJustifyField
              hideLabel
              value={(value as LayoutJustify) ?? "start"}
              onChange={onChange}
            />
          ),
        },
        gap: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.espacement_entre_elements",
            "Espacement entre éléments",
          ),
          render: ({ value, onChange }) => (
            <GapIconField
              label={cmsNew("cms.new.auto.label.espacement", "Espacement")}
              value={(value as LayoutGap) ?? "none"}
              onChange={onChange}
              customFieldName="gapCustomPx"
            />
          ),
        },
        paddingX: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.marge_interieure_horizontale",
            "Marge intérieure horizontale",
          ),
          options: [...getLayoutGapOptions()],
        },
        paddingY: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.marge_interieure_verticale",
            "Marge intérieure verticale",
          ),
          options: [...getLayoutGapOptions()],
        },
        maxWidth: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.largeur_maximale",
            "Largeur maximale",
          ),
          options: [...getLayoutMaxWidthOptions()].map(({ label, value }) => ({
            label,
            value,
          })),
        },
        background: {
          type: "select",
          label: cmsNew("cms.new.auto.label.fond", "Fond"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.aucun", "Aucun"),
              value: "none",
            },
            {
              label: cmsNew("cms.new.auto.label.gris_clair", "Gris clair"),
              value: "muted",
            },
            {
              label: cmsNew("cms.new.auto.label.sombre", "Sombre"),
              value: "dark",
            },
          ],
        },
        content: {
          type: "slot",
          allow: [...NESTABLE_SLOT_ALLOW],
        },
      },
      defaultProps: {
        direction: "column",
        align: "stretch",
        justify: "start",
        gap: "none",
        gapCustomPx: null,
        paddingX: "none",
        paddingY: "none",
        // Défaut WYSIWYG : plafonné à 1280 (= viewport Bureau du canvas) → le
        // rendu canvas et front coïncident. Mettre « Pleine largeur » pour bleed.
        maxWidth: "1280",
        background: "none",
        height: { value: null, unit: "px" },
        content: [],
      },
      // Rétro-compat : anciennes pages avec `padding` unique → paddingX/Y ;
      // `maxWidth: "2xl"` des premiers seeds de shell → preset `1536`.
      resolveData: (data) => {
        const p = data.props as {
          padding?: LayoutGap;
          paddingX?: LayoutGap;
          paddingY?: LayoutGap;
          maxWidth?: string;
        };
        const patch: Record<string, unknown> = {};
        if (p.padding != null && p.paddingX == null && p.paddingY == null) {
          patch.paddingX = p.padding;
          patch.paddingY = p.padding;
        }
        if (p.maxWidth === "2xl") patch.maxWidth = "1536";
        return { props: patch };
      },
      render: ({
        id,
        puck,
        direction,
        align,
        justify,
        gap,
        gapCustomPx,
        padding,
        paddingX,
        paddingY,
        height,
        wrap,
        content,
      }) => (
        <LayoutSlot
          slot={content}
          slotKey="content"
          componentId={id}
          variant="container"
          layoutDirection={direction}
          puck={puck}
          layoutClassName={containerClass({
            direction,
            align,
            justify,
            gap,
            gapCustomPx,
            padding,
            paddingX,
            paddingY,
            maxWidth: "full",
            background: "none",
            wrap,
          })}
          layoutStyle={{
            ...resolveContainerHeightStyle(height),
            ...containerGapStyle({ gap, gapCustomPx }),
          }}
        />
      ),
    },

    Columns: {
      label: cmsNew("cms.new.blocks.columns.label", "Colonnes"),
      fields: {
        columns: columnsLayoutCountField,
        gap: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.espacement_uniforme",
            "Espacement (uniforme)",
          ),
          render: ({ value, onChange }) => (
            <GapIconField
              label={cmsNew("cms.new.auto.label.espacement", "Espacement")}
              value={(value as LayoutGap) ?? "none"}
              onChange={onChange}
              customFieldName="gapCustomPx"
            />
          ),
        },
        gapRow: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.espacement_horizontal",
            "Espacement horizontal",
          ),
          render: ({ value, onChange }) => (
            <GapIconField
              label={cmsNew("cms.new.auto.label.horizontal", "Horizontal")}
              value={(value as LayoutGap) ?? "none"}
              onChange={onChange}
              customFieldName="gapRowCustomPx"
            />
          ),
        },
        gapCol: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.espacement_vertical",
            "Espacement vertical",
          ),
          render: ({ value, onChange }) => (
            <GapIconField
              label={cmsNew("cms.new.auto.label.vertical", "Vertical")}
              value={(value as LayoutGap) ?? "none"}
              onChange={onChange}
              customFieldName="gapColCustomPx"
            />
          ),
        },
        stackOnMobile: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.empiler_sur_mobile",
            "Empiler sur mobile",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.oui", "Oui"), value: true },
            { label: cmsNew("cms.new.auto.label.non", "Non"), value: false },
          ],
        },
        reverseOnMobile: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.inverser_lordre_sur_mobile",
            "Inverser l'ordre sur mobile",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.oui", "Oui"), value: true },
            { label: cmsNew("cms.new.auto.label.non", "Non"), value: false },
          ],
        },
        verticalAlign: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.alignement_vertical",
            "Alignement vertical",
          ),
          render: ({ value, onChange }) => (
            <FlexAlignField
              hideLabel
              value={(value as LayoutAlign) ?? "stretch"}
              onChange={onChange}
            />
          ),
        },
        col1Width: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.largeur_colonne_1",
            "Largeur colonne 1",
          ),
          options: [...getColumnTrackSizeOptions()],
        },
        col2Width: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.largeur_colonne_2",
            "Largeur colonne 2",
          ),
          options: [...getColumnTrackSizeOptions()],
        },
        col3Width: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.largeur_colonne_3",
            "Largeur colonne 3",
          ),
          options: [...getColumnTrackSizeOptions()],
        },
        col4Width: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.largeur_colonne_4",
            "Largeur colonne 4",
          ),
          options: [...getColumnTrackSizeOptions()],
        },
        col1: { type: "slot", allow: [...NESTABLE_SLOT_ALLOW] },
        col2: { type: "slot", allow: [...NESTABLE_SLOT_ALLOW] },
        col3: { type: "slot", allow: [...NESTABLE_SLOT_ALLOW] },
        col4: { type: "slot", allow: [...NESTABLE_SLOT_ALLOW] },
      },
      defaultProps: {
        columns: 2,
        gap: "none",
        gapCustomPx: null,
        gapRow: "none",
        gapCol: "none",
        gapRowCustomPx: null,
        gapColCustomPx: null,
        stackOnMobile: true,
        reverseOnMobile: false,
        verticalAlign: "stretch",
        col1Width: "1",
        col2Width: "1",
        col3Width: "1",
        col4Width: "1",
        col1: [],
        col2: [],
        col3: [],
        col4: [],
      },
      resolveData: (_data, params) => ({
        props: uniformGapResetPatch(params?.changed),
      }),
      render: ({
        id,
        puck,
        columns: rawColumns,
        gap,
        gapCustomPx,
        gapRow,
        gapCol,
        gapRowCustomPx,
        gapColCustomPx,
        stackOnMobile,
        reverseOnMobile,
        verticalAlign,
        col1Width,
        col2Width,
        col3Width,
        col4Width,
        col1,
        col2,
        col3,
        col4,
      }) => {
        const columns = normalizeColumnsCount(rawColumns);
        const slots = { col1, col2, col3, col4 };
        const keys = columnSlotKeys(columns);
        const widths = { col1Width, col2Width, col3Width, col4Width };
        const trackSizes = resolveColumnTrackSizes(columns, widths);
        const align = verticalAlign ?? "stretch";
        return (
          <ColumnsGrid
            columns={columns}
            gap={gap}
            gapCustomPx={gapCustomPx}
            gapRow={gapRow}
            gapCol={gapCol}
            gapRowCustomPx={gapRowCustomPx}
            gapColCustomPx={gapColCustomPx}
            stackOnMobile={stackOnMobile}
            reverseOnMobile={reverseOnMobile}
            verticalAlign={align}
            col1Width={col1Width}
            col2Width={col2Width}
            col3Width={col3Width}
            col4Width={col4Width}
          >
            {keys.map((key, index) => {
              const ColSlot = slots[key];
              return (
                <LayoutSlot
                  key={key}
                  slot={ColSlot}
                  slotKey={key}
                  componentId={id}
                  variant="column"
                  cellClassName={columnCellClassName(
                    trackSizes[index],
                    align,
                  )}
                  puck={puck}
                />
              );
            })}
          </ColumnsGrid>
        );
      },
    },

    Grid: {
      label: cmsNew("cms.new.blocks.grid.label", "Grille"),
      fields: {
        preset: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.preset_de_grille",
            "Preset de grille",
          ),
          options: [...getGridPresetOptions()],
        },
        columns: gridColumnCountField,
        gap: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.espacement_uniforme",
            "Espacement (uniforme)",
          ),
          render: ({ value, onChange }) => (
            <GapIconField
              label={cmsNew("cms.new.auto.label.espacement", "Espacement")}
              value={(value as LayoutGap) ?? "none"}
              onChange={onChange}
              customFieldName="gapCustomPx"
            />
          ),
        },
        gapRow: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.espacement_horizontal",
            "Espacement horizontal",
          ),
          render: ({ value, onChange }) => (
            <GapIconField
              label={cmsNew("cms.new.auto.label.horizontal", "Horizontal")}
              value={(value as LayoutGap) ?? "none"}
              onChange={onChange}
              customFieldName="gapRowCustomPx"
            />
          ),
        },
        gapCol: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.espacement_vertical",
            "Espacement vertical",
          ),
          render: ({ value, onChange }) => (
            <GapIconField
              label={cmsNew("cms.new.auto.label.vertical", "Vertical")}
              value={(value as LayoutGap) ?? "none"}
              onChange={onChange}
              customFieldName="gapColCustomPx"
            />
          ),
        },
        verticalAlign: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.alignement_vertical",
            "Alignement vertical",
          ),
          render: ({ value, onChange }) => (
            <FlexAlignField
              hideLabel
              value={(value as LayoutAlign) ?? "stretch"}
              onChange={onChange}
            />
          ),
        },
        columnWidths: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.largeurs_des_colonnes",
            "Largeurs des colonnes",
          ),
          render: ({ value, onChange }) => (
            <GridColumnWidthsField
              value={value as ColumnWidthWeight[] | undefined}
              onChange={onChange}
            />
          ),
        },
        content: {
          type: "slot",
          allow: [...NESTABLE_SLOT_ALLOW],
        },
      },
      defaultProps: {
        preset: "custom",
        columns: 3,
        gap: "none",
        gapCustomPx: null,
        gapRow: "none",
        gapCol: "none",
        gapRowCustomPx: null,
        gapColCustomPx: null,
        verticalAlign: "stretch",
        columnWidths: ["1", "1", "1"],
        content: [],
      },
      resolveData: (data, params) => {
        const preset = normalizeGridPreset(data.props.preset);
        const applied = applyGridPreset(preset);
        const n = applied
          ? applied.columns
          : Math.max(
              1,
              Math.min(
                MAX_CONTENT_COLUMNS,
                Math.floor(Number(data.props.columns) || 1),
              ),
            );
        const raw = data.props.columnWidths as
          | ColumnWidthWeight[]
          | undefined;
        const widths = applied
          ? applied.columnWidths
          : Array.isArray(raw)
            ? raw
            : [];
        const patch: Record<string, unknown> = {
          ...uniformGapResetPatch(params?.changed),
        };
        if (applied) {
          patch.columns = applied.columns;
          patch.columnWidths = applied.columnWidths;
        } else if (widths.length !== n) {
          patch.columnWidths = Array.from(
            { length: n },
            (_, i) => widths[i] ?? "1",
          );
        }
        return Object.keys(patch).length ? { props: patch } : { props: {} };
      },
      render: ({
        id,
        puck,
        preset,
        columns,
        gap,
        gapCustomPx,
        gapRow,
        gapCol,
        gapRowCustomPx,
        gapColCustomPx,
        verticalAlign,
        columnWidths,
        content,
      }) => {
        const align = verticalAlign ?? "stretch";
        return (
          <GridContainer
            columns={columns}
            gap={gap}
            gapCustomPx={gapCustomPx}
            gapRow={gapRow}
            gapCol={gapCol}
            gapRowCustomPx={gapRowCustomPx}
            gapColCustomPx={gapColCustomPx}
            columnWidths={columnWidths}
            verticalAlign={align}
            preset={normalizeGridPreset(preset)}
          >
            <LayoutSlot
              slot={content}
              slotKey="content"
              componentId={id}
              variant="grid"
              puck={puck}
            />
          </GridContainer>
        );
      },
    },

    Spacer: {
      label: cmsNew("cms.new.auto.label.espace", "Espace"),
      fields: {
        size: {
          type: "number",
          label: cmsNew("cms.new.auto.label.hauteur_px", "Hauteur (px)"),
          min: 0,
          max: 200,
        },
      },
      defaultProps: { size: 48 },
      render: ({ size }) => <div style={{ height: `${size}px` }} />,
    },
  } satisfies Pick<RawComponentsConfig, "Container" | "Columns" | "Grid" | "Spacer">;
}
